import { DEFAULT_STO_COMPLETION_TIME } from 'modules/campaigns/lib/constants';

/**
 * Returns whether STO and quiet hours overlaps
 */
export const getIsQuietHoursOverlappingWithSTO = (
  quietHours?: { startTime: string; endTime: string } | null,
  windowHours?: number,
) => {
  const stoWindowInHours = Number(windowHours || DEFAULT_STO_COMPLETION_TIME);

  if (
    !quietHours ||
    !windowHours ||
    Number.isNaN(stoWindowInHours) ||
    !(quietHours?.startTime && quietHours?.endTime)
  ) {
    return false;
  }

  const { startTime, endTime } = quietHours || {};

  const start = startTime.split(':');
  const end = endTime.split(':');

  const startHours = parseInt(start?.[0], 10);
  const startMinutes = parseInt(start?.[1], 10);

  const endHours = parseInt(end?.[0], 10);
  const endMinutes = parseInt(end?.[1], 10);

  const quietHoursStartIn24HourDecimalClock = startHours + startMinutes / 60; // decimal 24-hour clock time (ex: 22.25 -> 10:15 PM)
  const quietHoursEndIn24HourDecimalClock = endHours + endMinutes / 60; // decimal 24-hour clock time (ex: 22.25 -> 10:15 PM)

  // window = endTime - startTime
  let quietHoursWindow = Math.abs(
    quietHoursEndIn24HourDecimalClock - quietHoursStartIn24HourDecimalClock,
  );

  // For quiet hours that starts at a higher time (ex: 10:00 AM - 9:00 AM) than it ends, the duration is found using the following formula: `24 - absoluteValue(endTime - startTime)`
  if (quietHoursStartIn24HourDecimalClock > quietHoursEndIn24HourDecimalClock) {
    quietHoursWindow = 24 - quietHoursWindow;
  }

  /**
   * 1. If quiet hours exist for one day for certain hours, it will also exist for all days for those same hours (assuming it’s on for all those days).
   * For example, we can also think of the quiet hours range 8:00 PM-8:00 AM, which is over two separate days as two different time ranges over one day (i.e., 12:00 AM-8:00 AM and 8:00 PM-12:00 AM)
   *
   * 2. Even though STO starts at the campaign’s `sendTime`, there are some time ranges where we can determine whether there will be an overlap between STO and quiet hours without the `sendTime`.
   * Using the same example from 1, we know that for the time range between 12:00 AM-8:00 AM and 8:00 PM-12:00 AM (8:00 AM-8:00 PM, a 12-hour window),
   * if the time window **without** STO is **less than or equal** to the quiet hours window, then they’ll have to overlap.
   *
   * formula: `totalHoursWithoutSTOInDay = hoursInDay - stoWindowInHours`
   */
  const totalHoursWithoutSTOInDay = 24 - stoWindowInHours;
  const isOverlapping = totalHoursWithoutSTOInDay <= quietHoursWindow; // inclusive overlapping

  return isOverlapping;
};
